// src/components/projected-profile/DimensionRadar.jsx

import { Box } from '@mantine/core'

function getVertices(cx, cy, radius, count) {
  return Array.from({ length: count }, (_, i) => {
    const angle = ((i * (360 / count)) - 90) * (Math.PI / 180)
    return {
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    }
  })
}

function polygonPoints(vertices) {
  return vertices.map((v) => `${v.x},${v.y}`).join(' ')
}

/**
 * Pentagon radar chart with configurable dimension labels.
 *
 * @param {Array} dimensions - [{ key, label }] — the axes
 * @param {Object} scores - { [key]: number } — primary scores (0-100)
 * @param {Object} [ghostScores] - optional second polygon (dashed, faded)
 * @param {string} [color='rgba(0, 229, 255, 0.7)'] - primary polygon stroke color
 * @param {string} [fillColor='rgba(0, 229, 255, 0.12)'] - primary polygon fill
 * @param {string} [ghostColor='rgba(107, 124, 158, 0.4)'] - ghost polygon stroke
 * @param {number} [size=280] - SVG size in pixels
 */
export function DimensionRadar({
  dimensions,
  scores,
  ghostScores,
  color = 'rgba(0, 229, 255, 0.7)',
  fillColor = 'rgba(0, 229, 255, 0.12)',
  ghostColor = 'rgba(107, 124, 158, 0.4)',
  size = 280,
}) {
  if (!dimensions || !scores) return null

  const count = dimensions.length
  const cx = size / 2
  const cy = size / 2
  const maxRadius = size * 0.38
  const labelRadius = size * 0.47

  const gridRings = [0.25, 0.5, 0.75, 1.0]
  const outerVertices = getVertices(cx, cy, maxRadius, count)
  const labelVertices = getVertices(cx, cy, labelRadius, count)

  function scorePolygon(scoreObj) {
    return dimensions.map((dim, i) => {
      const score = scoreObj[dim.key] || 0
      const fraction = Math.min(100, Math.max(0, score)) / 100
      const angle = ((i * (360 / count)) - 90) * (Math.PI / 180)
      return {
        x: cx + maxRadius * fraction * Math.cos(angle),
        y: cy + maxRadius * fraction * Math.sin(angle),
      }
    })
  }

  const primaryVertices = scorePolygon(scores)
  const ghostVertices = ghostScores ? scorePolygon(ghostScores) : null

  return (
    <Box style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Grid rings */}
        {gridRings.map((fraction) => (
          <polygon
            key={fraction}
            points={polygonPoints(getVertices(cx, cy, maxRadius * fraction, count))}
            fill="none"
            stroke="rgba(0, 229, 255, 0.08)"
            strokeWidth={1}
          />
        ))}

        {/* Axis lines */}
        {outerVertices.map((v, i) => (
          <line key={i} x1={cx} y1={cy} x2={v.x} y2={v.y} stroke="rgba(0, 229, 255, 0.06)" strokeWidth={1} />
        ))}

        {/* Ghost polygon (e.g. Week 12 scores) */}
        {ghostVertices && (
          <polygon
            points={polygonPoints(ghostVertices)}
            fill="none"
            stroke={ghostColor}
            strokeWidth={1}
            strokeDasharray="4,3"
          />
        )}

        {/* Primary polygon */}
        <polygon
          points={polygonPoints(primaryVertices)}
          fill={fillColor}
          stroke={color}
          strokeWidth={2}
          style={{ filter: 'drop-shadow(0 0 6px rgba(0, 229, 255, 0.3))' }}
        />

        {/* Score dots */}
        {primaryVertices.map((v, i) => (
          <circle
            key={i}
            cx={v.x}
            cy={v.y}
            r={3.5}
            fill="#00e5ff"
            style={{ filter: 'drop-shadow(0 0 4px rgba(0, 229, 255, 0.6))' }}
          />
        ))}

        {/* Labels + scores */}
        {dimensions.map((dim, i) => {
          const lv = labelVertices[i]
          const score = scores[dim.key] || 0
          return (
            <g key={dim.key}>
              <text
                x={lv.x}
                y={lv.y - 6}
                textAnchor="middle"
                dominantBaseline="auto"
                fill="rgba(0, 229, 255, 0.7)"
                fontSize={9}
                fontFamily="Orbitron, sans-serif"
                fontWeight={700}
              >
                {dim.label}
              </text>
              <text
                x={lv.x}
                y={lv.y + 9}
                textAnchor="middle"
                dominantBaseline="auto"
                fill="rgba(224, 232, 240, 0.6)"
                fontSize={11}
                fontFamily="Rajdhani, sans-serif"
                fontWeight={600}
              >
                {score}
              </text>
            </g>
          )
        })}

        {/* Center overall score */}
        {scores.overall != null && (
          <>
            <text x={cx} y={cy - 6} textAnchor="middle" fill="#00e5ff" fontSize={24} fontFamily="Orbitron, sans-serif" fontWeight={700}>
              {scores.overall}
            </text>
            <text x={cx} y={cy + 12} textAnchor="middle" fill="rgba(224, 232, 240, 0.5)" fontSize={8} fontFamily="Orbitron, sans-serif" fontWeight={600} letterSpacing={2}>
              OVERALL
            </text>
          </>
        )}
      </svg>
    </Box>
  )
}
